import { createContext, useCallback, useMemo, useState, useEffect } from 'react';
import { isOnlyPlatformBaseUrl } from '../utils/urlUtils';

// Domain-based default state - organized by WHAT it represents, not WHERE it appears
const defaultWizardData = {
  company: {
    name: '',
    email: '',
    managerName: '',
    managerNumber: '',
    website: 'https://',
    description: '',
    backgroundColor: '#FFFFFF',
    companyId: null,
    companyLogo: null,
    facebookPage: '',
    instagramProfile: '',
    pinterestProfile: ''
  },
  logo: {
    url: null
  },
  dealSelection: {
    selectedDeal: '',
    includeCoupon: true
  },
  popinBudget: {
    visitors: null,
    price: null,
    popinRateId: null
  },
  couponType: {
    activeTab: 1,
    deal: '',
    dealUnit: 'Free',
    dealItem: 'T-Shirt',
    currency: 'zar'
  },
  expiration: {
    cost: 0,
    durationType: 'days',
    duration: 0,
    agentFee: 0
  },
  couponDetails: {
    code: '',
    website: '',
    phone: '',
    email: '',
    description: ''
  },
  terms: {
    terms: [],
    instructions: [],
    customTerms: [],
    customInstructions: []
  },
  locations: {
    list: [],
    isOnlineOnly: false,
    countries: [],
    isGlobal: false
  },
  categories: {
    selected: []
  },
  meta: {
    companyId: null,
    couponId: null,
    creatorToken: null,
    popinId: null,
    popinCreatorToken: null,
    popinManageToken: null,
    userCountry: 'us',
    affiliateCode: null
  }
};

const WizardDataContext = createContext({
  wizardData: defaultWizardData,
  updateData: (domainKey, newData) => {},
  clearAllData: () => {},
  createCompanyDraft: () => {}
});

export function WizardDataProvider(props) {
  // Load initial state from localStorage
  const getInitialState = () => {
    try {
      const stored = localStorage.getItem('wizardData');
      let merged = defaultWizardData;
      if (stored) {
        const parsed = JSON.parse(stored);
        merged = {
          ...defaultWizardData,
          ...parsed,
          company: { ...defaultWizardData.company, ...(parsed.company || {}) },
          logo: { ...defaultWizardData.logo, ...(parsed.logo || {}) },
          dealSelection: { ...defaultWizardData.dealSelection, ...(parsed.dealSelection || {}) },
          popinBudget: { ...defaultWizardData.popinBudget, ...(parsed.popinBudget || {}) },
          couponType: { ...defaultWizardData.couponType, ...(parsed.couponType || {}) },
          expiration: { ...defaultWizardData.expiration, ...(parsed.expiration || {}) },
          couponDetails: { ...defaultWizardData.couponDetails, ...(parsed.couponDetails || {}) },
          terms: { ...defaultWizardData.terms, ...(parsed.terms || {}) },
          locations: { ...defaultWizardData.locations, ...(parsed.locations || {}) },
          categories: { ...defaultWizardData.categories, ...(parsed.categories || {}) },
          meta: {
            ...defaultWizardData.meta,
            ...(parsed.meta || {}),
            creatorToken: parsed.meta?.creatorToken ?? defaultWizardData.meta.creatorToken,
            popinId: parsed.meta?.popinId ?? defaultWizardData.meta.popinId,
            popinCreatorToken: parsed.meta?.popinCreatorToken ?? defaultWizardData.meta.popinCreatorToken,
            popinManageToken: parsed.meta?.popinManageToken ?? defaultWizardData.meta.popinManageToken
          }
        };
      }
      // Sync current affiliate from localStorage into meta so it persists with wizardData
      const storedAffiliateCode = localStorage.getItem('deliva_affiliate_code');
      if (storedAffiliateCode) {
        merged = {
          ...merged,
          meta: {
            ...merged.meta,
            affiliateCode: storedAffiliateCode || merged.meta.affiliateCode
          }
        };
      }
      return merged;
    } catch (error) {
      console.error('Error loading wizardData from localStorage:', error);
      return defaultWizardData;
    }
  };

  const [wizardData, setWizardData] = useState(getInitialState);

  // Single localStorage sync effect - replaces 15+ individual useEffects
  useEffect(() => {
    try {
      localStorage.setItem('wizardData', JSON.stringify(wizardData));
    } catch (error) {
      console.error('Error saving wizardData to localStorage:', error);
    }
  }, [wizardData]);

  // Generic update function - works for any domain key
  // Memoized to keep consumer effects from re-triggering on every render.
  const updateData = useCallback((domainKey, newData) => {
    setWizardData((prev) => {
      // Handle both object merge and direct replacement
      if (typeof newData === 'object' && newData !== null && !Array.isArray(newData)) {
        return {
          ...prev,
          [domainKey]: { ...prev[domainKey], ...newData }
        };
      }
      return {
        ...prev,
        [domainKey]: newData
      };
    });
  }, []);

  // Clear all data and reset to defaults
  const clearAllData = useCallback(() => {
    setWizardData(defaultWizardData);
    // Clear localStorage
    try {
      localStorage.removeItem('wizardData');
    } catch (error) {
      console.error('Error clearing wizardData from localStorage:', error);
    }
  }, []);

  // Create company draft - migrated from DataContext
  const createCompanyDraft = useCallback(async () => {
    const company = wizardData.company;
    const payload = {
      company_name: company.name,
      background_color: company.backgroundColor || '#FFFFFF',
      email_address: company.email,
      phone_number: company.managerNumber,
      description: company.description,
      contact_person: company.managerName,
      website: company.website,
      listingType: 'coupon'
    };

    // Add social media fields only if user added something beyond the platform base URL
    if (company.facebookPage && company.facebookPage.trim() !== '' && !isOnlyPlatformBaseUrl(company.facebookPage, 'facebook')) {
      payload.facebook_page = company.facebookPage;
    }
    if (company.instagramProfile && company.instagramProfile.trim() !== '' && !isOnlyPlatformBaseUrl(company.instagramProfile, 'instagram')) {
      payload.instagram_profile = company.instagramProfile;
    }
    if (company.pinterestProfile && company.pinterestProfile.trim() !== '' && !isOnlyPlatformBaseUrl(company.pinterestProfile, 'pinterest')) {
      payload.pinterest_profile = company.pinterestProfile;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URI}/v3/advertising`, {
        method: 'POST',
        mode: 'cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();

      if (data.status === 'success' || res.ok) {
        const companyId = data.data?.id || data.id || data.company_id;
        updateData('meta', { companyId: companyId });
        console.log('Company ID:', companyId);
        return { success: true, companyId: companyId };
      } else {
        return { success: false, error: data.message || 'Failed to create company' };
      }
    } catch (error) {
      console.error('Error creating company draft:', error);
      return { success: false, error: 'Network error occurred' };
    }
  }, [wizardData.company, updateData]);

  const context = useMemo(() => {
    return {
      wizardData,
      updateData,
      clearAllData,
      createCompanyDraft
    };
  }, [wizardData, updateData, clearAllData, createCompanyDraft]);

  return (
    <WizardDataContext.Provider value={context}>
      {props.children}
    </WizardDataContext.Provider>
  );
}

export default WizardDataContext;
